import * as React from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft, BookOpen, EyeOff, Loader2, Pencil, Plus, Trash2 } from 'lucide-react'
import { sb } from '@/lib/supabase'
import { useQuery } from '@/hooks/use-query'
import { GOAL_ICONS, goalIcon } from '@/lib/goal-icons'
import type { CommonGoal, Presentation } from '@/lib/types'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { EmptyState, ErrorNote, Loading, Page } from '@/components/page'
import { useDirty, useDiscardGuard } from '@/hooks/use-unsaved'
import { DiscardPrompt } from '@/components/discard-prompt'

/**
 * The common-goals catalog — DPL's list of what software teams typically want,
 * and which education materials serve each one.
 *
 * DPL-admin only, and the database agrees: `common_goals_admin` in 0027 refuses
 * every write from anyone else, so hiding the controls here is courtesy rather
 * than the control.
 *
 * **Editing a goal here does NOT edit any team's dashboard.** Picking a catalog
 * goal copies its wording into that team's own `team_goals` row, which the lead
 * then usually rewrites — see the header of 0027 for why that's deliberate. What
 * an edit here changes is what the NEXT team sees in the picker, plus which
 * topics are listed as supporting the goal for teams that already picked it.
 * That's worth knowing before rewording something eleven teams are using.
 */
export function CommonGoalsPage() {
  const [editing, setEditing] = React.useState<CommonGoal | 'new' | null>(null)

  const { data, loading, error, refetch } = useQuery(async () => {
    const [goals, decks, links] = await Promise.all([
      // Admins see retired goals too — this is the page for un-retiring one.
      sb().from('common_goals').select('*').order('display_order'),
      sb().from('presentations').select('*').eq('active', true).order('display_order'),
      sb().from('common_goal_presentations').select('goal_id, presentation_id'),
    ])
    if (goals.error) throw goals.error
    if (decks.error) throw decks.error
    if (links.error) throw links.error

    const byGoal = new Map<string, string[]>()
    for (const l of (links.data ?? []) as { goal_id: string; presentation_id: string }[]) {
      byGoal.set(l.goal_id, [...(byGoal.get(l.goal_id) ?? []), l.presentation_id])
    }
    return {
      goals: (goals.data ?? []) as CommonGoal[],
      decks: (decks.data ?? []) as Presentation[],
      byGoal,
    }
  }, [])

  const goals = data?.goals ?? []
  const decks = data?.decks ?? []

  // A deck no goal reaches is a deck no team is ever pointed at. The seed asserts
  // this in SQL (0028); saying it here too is what catches a deck added later,
  // because nobody re-runs a migration to find out.
  const unassigned = React.useMemo(() => {
    if (!data) return []
    const assigned = new Set([...data.byGoal.values()].flat())
    return decks.filter((d) => !assigned.has(d.id))
  }, [data, decks])

  return (
    <Page
      title="Common goals"
      description="What software teams typically want, and the topics that serve each one. Teams pick from this list — or write their own."
      wide
      actions={
        <Button onClick={() => setEditing('new')}>
          <Plus /> New goal
        </Button>
      }
    >
      <Button variant="ghost" size="sm" className="mb-4 -ml-2" asChild>
        <Link to="/mission-control">
          <ArrowLeft /> Mission Control
        </Link>
      </Button>

      {error && <ErrorNote error={error} />}

      {unassigned.length > 0 && (
        <Card className="mb-5 border-warning/40 bg-warning/5 shadow-none">
          <CardContent className="py-4 text-sm">
            <p className="font-medium">
              {unassigned.length} {unassigned.length === 1 ? 'topic serves' : 'topics serve'} no goal
            </p>
            <p className="mt-1 text-muted-foreground">
              No team will be pointed at {unassigned.length === 1 ? 'it' : 'them'} from a goal:{' '}
              {unassigned.map((d) => d.title).join(', ')}.
            </p>
          </CardContent>
        </Card>
      )}

      {loading && !data ? (
        <Loading />
      ) : goals.length === 0 ? (
        <EmptyState
          icon={BookOpen}
          title="No common goals yet"
          description="Add the goals teams ask for most, then assign the topics that help with each."
          action={
            <Button onClick={() => setEditing('new')}>
              <Plus /> New goal
            </Button>
          }
        />
      ) : (
        <div className="space-y-2.5">
          {goals.map((g) => {
            const Icon = goalIcon(g.icon)
            const mine = data?.byGoal.get(g.id) ?? []
            return (
              <Card key={g.id} className={cn('shadow-none', !g.active && 'opacity-60')}>
                <CardContent className="flex flex-wrap items-start gap-4 p-4">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                    <Icon className="h-5 w-5 text-primary" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium">{g.title}</span>
                      {g.short_label && (
                        <Badge variant="outline" className="text-[10px] font-normal">
                          {g.short_label}
                        </Badge>
                      )}
                      {!g.active && (
                        <Badge variant="muted" className="gap-1 text-[10px]">
                          <EyeOff className="h-3 w-3" /> retired
                        </Badge>
                      )}
                    </div>
                    {g.description && (
                      <p className="mt-0.5 text-sm text-muted-foreground">{g.description}</p>
                    )}
                    {/* The assignment IS the feature — showing a count would
                        make an admin open the dialog to find out what's in it. */}
                    <div className="mt-2 flex flex-wrap gap-1">
                      {mine.length === 0 ? (
                        <span className="text-xs text-warning">No topics assigned yet</span>
                      ) : (
                        decks
                          .filter((d) => mine.includes(d.id))
                          .map((d) => (
                            <Badge key={d.id} variant="secondary" className="text-[10px] font-normal">
                              {d.title}
                            </Badge>
                          ))
                      )}
                    </div>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => setEditing(g)}>
                    <Pencil /> Edit
                  </Button>
                </CardContent>
              </Card>
            )
          })}
        </div>
      )}

      {editing && (
        <CommonGoalEditor
          goal={editing === 'new' ? undefined : editing}
          siblings={goals}
          decks={decks}
          assigned={editing === 'new' ? [] : (data?.byGoal.get(editing.id) ?? [])}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            void refetch()
          }}
        />
      )}
    </Page>
  )
}

function CommonGoalEditor({
  goal,
  siblings,
  decks,
  assigned,
  onClose,
  onSaved,
}: {
  goal?: CommonGoal
  siblings: CommonGoal[]
  decks: Presentation[]
  assigned: string[]
  onClose: () => void
  onSaved: () => void
}) {
  const [title, setTitle] = React.useState(goal?.title ?? '')
  const [description, setDescription] = React.useState(goal?.description ?? '')
  const [icon, setIcon] = React.useState(goal?.icon ?? GOAL_ICONS[0].name)
  const [shortLabel, setShortLabel] = React.useState(goal?.short_label ?? '')
  const [active, setActive] = React.useState(goal?.active ?? true)
  const [picked, setPicked] = React.useState<Set<string>>(new Set(assigned))
  const [busy, setBusy] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  const dirty = useDirty({ title, description, icon, shortLabel, active, picked: [...picked] })
  const guard = useDiscardGuard(dirty, onClose)

  function toggle(id: string) {
    setPicked((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  async function save(e: React.FormEvent) {
    e.preventDefault()
    if (!title.trim()) return setError('Give the goal a title.')
    setBusy(true)
    setError(null)
    try {
      const row = {
        title: title.trim(),
        description: description.trim() || null,
        short_label: shortLabel.trim() || null,
        icon,
        active,
      }
      let goalId = goal?.id
      if (goal) {
        const { error: err } = await sb().from('common_goals').update(row).eq('id', goal.id)
        if (err) throw err
      } else {
        // Derived from the title rather than asked for: the slug is a stable key
        // for the seed's upsert and for nothing a human needs to type. Suffixed
        // on collision so two goals worded similarly don't fail the unique index
        // with an error an admin can't act on.
        const base = slugify(title)
        const taken = new Set(siblings.map((s) => s.slug.toLowerCase()))
        let slug = base
        for (let i = 2; taken.has(slug); i++) slug = `${base}-${i}`
        const { data: made, error: err } = await sb()
          .from('common_goals')
          .insert({
            ...row,
            slug,
            display_order: Math.max(0, ...siblings.map((s) => s.display_order)) + 10,
          })
          .select('id')
          .single()
        if (err) throw err
        goalId = (made as { id: string }).id
      }
      if (!goalId) throw new Error('the goal was saved but returned no id')

      // Replace the assignment set. Delete-then-insert rather than diffing:
      // fourteen rows at most, and a diff has two more ways to be wrong.
      const { error: delErr } = await sb()
        .from('common_goal_presentations')
        .delete()
        .eq('goal_id', goalId)
      if (delErr) throw delErr
      if (picked.size > 0) {
        const { error: insErr } = await sb()
          .from('common_goal_presentations')
          .insert([...picked].map((presentation_id) => ({ goal_id: goalId, presentation_id })))
        if (insErr) throw insErr
      }
      onSaved()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  async function remove() {
    if (!goal) return
    if (
      !window.confirm(
        `Delete “${goal.title}”? Teams that already picked it keep their goal and its wording — it just stops being linked to these topics. Retiring it instead keeps that link.`,
      )
    )
      return
    setBusy(true)
    const { error: err } = await sb().from('common_goals').delete().eq('id', goal.id)
    setBusy(false)
    if (err) return setError(err.message)
    onSaved()
  }

  return (
    <Dialog open onOpenChange={(o) => !o && guard.requestClose()}>
      <DialogContent className="sm:max-w-2xl">
        <DiscardPrompt guard={guard} what="this common goal" />
        <form onSubmit={save}>
          <DialogHeader>
            <DialogTitle>{goal ? 'Edit common goal' : 'New common goal'}</DialogTitle>
            <DialogDescription>
              Phrase it as an outcome a team wants, not as a topic — “Ship without a release
              weekend”, not “Releases”. Teams see this in their goal picker and can reword it after
              picking.
            </DialogDescription>
          </DialogHeader>

          <div className="my-4 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="cg-title">Goal</Label>
              <Input
                id="cg-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Ship without a release weekend"
                autoFocus
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="cg-desc">What it means</Label>
              <Textarea
                id="cg-desc"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                placeholder="Releases that are boring: automated, repeatable, small enough to be a Tuesday afternoon."
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="cg-short">Short label</Label>
              <Input
                id="cg-short"
                value={shortLabel}
                onChange={(e) => setShortLabel(e.target.value)}
                maxLength={24}
                placeholder="Predictable releases"
              />
              {/* Two or three words, because this is what appears as a chip on
                  the Topics list — where the sentence above would be the whole
                  row. Left empty, that chip falls back to the full title. */}
              <p className="text-xs text-muted-foreground">
                Shown as a chip next to the topics that serve this goal. Falls back to the full
                title if left empty.
              </p>
            </div>

            <div className="space-y-1.5">
              <Label>Icon</Label>
              <div className="flex flex-wrap gap-1.5">
                {GOAL_ICONS.map((opt) => {
                  const Icon = opt.icon
                  const on = icon === opt.name
                  return (
                    <button
                      key={opt.name}
                      type="button"
                      onClick={() => setIcon(opt.name)}
                      aria-label={opt.label}
                      aria-pressed={on}
                      title={opt.label}
                      className={cn(
                        'flex h-9 w-9 items-center justify-center rounded-md border transition-colors',
                        on
                          ? 'border-primary bg-primary/10 text-primary'
                          : 'border-input text-muted-foreground hover:bg-muted',
                      )}
                    >
                      <Icon className="h-4 w-4" />
                    </button>
                  )
                })}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>Topics that serve this goal</Label>
              <p className="text-xs text-muted-foreground">
                A topic can serve several goals. Teams that pick this goal see these as the
                material behind it.
              </p>
              <div className="mt-1 max-h-[34vh] space-y-1 overflow-y-auto rounded-md border p-2">
                {decks.map((d) => {
                  const on = picked.has(d.id)
                  return (
                    <button
                      key={d.id}
                      type="button"
                      onClick={() => toggle(d.id)}
                      aria-pressed={on}
                      className={cn(
                        'flex w-full items-center gap-2.5 rounded px-2 py-1.5 text-left text-sm transition-colors',
                        on ? 'bg-primary/5' : 'hover:bg-muted',
                      )}
                    >
                      <span
                        className={cn(
                          'flex h-4 w-4 shrink-0 items-center justify-center rounded border text-[10px]',
                          on ? 'border-primary bg-primary text-primary-foreground' : 'border-input',
                        )}
                      >
                        {on && '✓'}
                      </span>
                      <span className="min-w-0 flex-1 truncate">{d.title}</span>
                    </button>
                  )
                })}
              </div>
            </div>

            {/* Retiring keeps every existing link intact and only removes the
                goal from the picker, which is almost always what someone
                reaching for Delete actually wants. */}
            <label className="flex cursor-pointer items-start gap-2 text-sm">
              <input
                type="checkbox"
                checked={!active}
                onChange={(e) => setActive(!e.target.checked)}
                className="mt-0.5"
              />
              <span>
                Retire this goal
                <span className="block text-xs text-muted-foreground">
                  Hidden from the picker. Teams that already picked it keep it, and its topics stay
                  linked.
                </span>
              </span>
            </label>
          </div>

          {error && <ErrorNote error={error} />}

          <DialogFooter className="items-center gap-2 sm:justify-between">
            {goal ? (
              <Button type="button" variant="ghost" onClick={() => void remove()} disabled={busy}>
                <Trash2 /> Delete
              </Button>
            ) : (
              <span />
            )}
            <div className="flex gap-2">
              <Button type="button" variant="ghost" onClick={guard.requestClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={busy}>
                {busy && <Loader2 className="animate-spin" />}
                {goal ? 'Save' : 'Add goal'}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

/** Title → a stable, lowercase, hyphenated key. Never shown to anyone. */
function slugify(s: string): string {
  const out = s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60)
  // A title of pure punctuation would otherwise produce an empty slug and a
  // confusing not-null violation.
  return out || 'goal'
}
